import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import GroupsIcon from '@mui/icons-material/Groups';
import RuleScreen from '../../shared/RuleScreen';
import SundargarhFilterBar from '../../shared/SundargarhFilterBar';
import StatTile from '../../shared/StatTile';
import DataTable from '../../shared/DataTable';
import Pagination, { normalizePagination } from '../../shared/Pagination';
import DetailDrawer from '../../shared/DetailDrawer';
import { buildParams } from '../../shared/apiParams';

const API_URL = '/api/survey'; // Express backend (services/sundargarhData.js), same live-Kobo data source as the old Vercel functions
const EMPTY = { ulbs: [], wards: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const calcDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms)) return 'Invalid';
  const min = Math.abs(ms / 60000);
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'ulb', label: 'By ULB', col: 'ULB' },
  { key: 'ward', label: 'By Ward', col: 'Ward' },
];

const pctCell = (b, highlight) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: highlight ? 'var(--danger)' : 'var(--muted)', fontWeight: highlight ? 700 : 400, marginLeft: 4 }}>
      ({b?.pct || 0}%)
    </span>
  </>
);

// Same-shape counterpart to the internal Indicators.js, parameterized by
// rule.fieldKey — used for skill training / agriculture / livestock / own
// house, each a Yes/No field on the live Kobo submission.
export default function SundargarhIndicators({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [field, setField] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, value: null, records: [], loading: false });

  const tab = TABS.find((t) => t.key === activeTab);
  const fieldKey = rule?.fieldKey;

  const fetchData = useCallback(async () => {
    if (!fieldKey) return;
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { field: fieldKey, page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/sundargarh/rules/indicators/analytics?${params}`);
      if (res.data?.success) {
        setField((res.data.fields || [])[0] || null);
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching Sundargarh indicator data:', err);
    } finally {
      setLoading(false);
    }
  }, [fieldKey, activeTab, applied, page, pageSize]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalSurveys = summary?.totalSurveys ?? rows.reduce((s, r) => s + (Number(r.totalSurveys) || 0), 0);
  const noPct = summary?.fields?.[fieldKey]?.no?.pct ?? 0;
  const yesPct = summary?.fields?.[fieldKey]?.yes?.pct ?? 0;
  const noDataPct = summary?.fields?.[fieldKey]?.noData?.pct ?? 0;

  const tiles = (
    <>
      <StatTile label="Total Surveys" value={totalSurveys} icon={GroupsIcon} accent="var(--primary)" />
      <StatTile
        label={`${field?.label || rule?.title || 'Field'} — No`}
        value={`${noPct}% No`}
        icon={FactCheckIcon}
        accent={noPct >= 50 ? 'var(--danger)' : noPct >= 25 ? 'var(--warning)' : 'var(--info)'}
        sub={noDataPct > 0 ? `${yesPct}% Yes · ${noDataPct}% No Data` : `${yesPct}% Yes`}
      />
    </>
  );

  const openDrilldown = async (row, value) => {
    setDrawer({ open: true, row, value, records: [], loading: true });
    try {
      const params = buildParams(applied, { extra: { groupBy: activeTab, groupValue: row.name, field: fieldKey, value, limit: 100 } });
      const res = await axios.get(`${API_URL}/sundargarh/rules/indicators/details?${params}`);
      setDrawer({ open: true, row, value, records: res.data?.data || [], loading: false });
    } catch (err) {
      console.error('Error fetching indicator detail records:', err);
      setDrawer({ open: true, row, value, records: [], loading: false });
    }
  };

  const indicatorCell = (row, value) => (
    <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(row, value); }}>
      {pctCell(row.fields?.[fieldKey]?.[value], value === 'no')}
    </span>
  );

  const hasBlanks = (summary?.fields?.[fieldKey]?.noData?.count ?? 0) > 0;
  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalSurveys', label: 'Total Surveys', align: 'center', render: (r) => <span className="cell-strong">{r.totalSurveys || 0}</span> },
    { key: 'yes', label: `${field?.label || ''} — Yes %`, align: 'center', render: (r) => indicatorCell(r, 'yes') },
    { key: 'no', label: `${field?.label || ''} — No %`, align: 'center', render: (r) => indicatorCell(r, 'no') },
    ...(hasBlanks ? [{ key: 'noData', label: `${field?.label || ''} — No Data %`, align: 'center', render: (r) => indicatorCell(r, 'noData') }] : []),
  ];

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    { key: 'household_id', label: 'Household ID', render: (r) => r.household_id || '—' },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    { key: 'fieldValue', label: field?.label || 'Value', render: (r) => r.fieldValue ?? '—' },
    { key: 'ulb', label: 'ULB', render: (r) => r.ulb || '—' },
    { key: 'ward', label: 'Ward', render: (r) => r.ward || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
    { key: 'end', label: 'End Time', render: (r) => formatDate(r.end) },
    { key: 'duration', label: 'Duration', align: 'center', render: (r) => calcDuration(r.start, r.end) },
  ];

  return (
    <RuleScreen
      title={rule?.title || field?.label || 'Household Indicators'}
      subtitle={`Yes/No breakdown for ${field?.label || rule?.title || 'this field'}, from live KoboToolbox data.`}
      icon={FactCheckIcon}
      backTo={rule?.category ? `/sundargarh-category/${rule.category}` : undefined}
      backLabel="Back"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <SundargarhFilterBar
            onApply={(v) => { setApplied(v); setPage(1); }}
            onClear={() => { setApplied(EMPTY); setPage(1); }}
            loading={loading}
          />
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">{field?.label || 'Indicator'} by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          getRowKey={(r, i) => `${r.name}-${i}`}
        />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${field?.label || ''} (${drawer.value === 'yes' ? 'Yes' : drawer.value === 'noData' ? 'No Data' : 'No'})` : ''}
        sections={
          drawer.row
            ? [{
                title: 'Household',
                items: [{ label: field?.label || 'Value', value: `${drawer.row.fields?.[fieldKey]?.yes?.count || 0} Yes / ${drawer.row.fields?.[fieldKey]?.no?.count || 0} No` }],
              }]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <DataTable
            columns={drawerRecordColumns}
            rows={drawer.records}
            loading={drawer.loading}
            emptyMessage="No households found."
            getRowKey={(r, i) => r.uuid || i}
          />
        </div>
      </DetailDrawer>
    </RuleScreen>
  );
}
